import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar,
} from "recharts";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function formatValue(val) {
  return val ?? "-";
}

// ─── Color Palette ────────────────────────────────────────────────────────────
const CHART_COLORS = {
  blue: "#3b82f6",
  green: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  purple: "#8b5cf6",
  sky: "#0ea5e9",
  slate: "#64748b",
  teal: "#14b8a6",
};

const PIE_COLORS = [CHART_COLORS.blue, CHART_COLORS.slate];
const INCIDENT_SEVERITY_COLORS = {
  high: CHART_COLORS.red,
  medium: CHART_COLORS.amber,
  low: CHART_COLORS.green,
  critical: "#dc2626",
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-lg text-sm"
      style={{ fontFamily: FONT_UI }}
    >
      {label && <p className="text-slate-500 mb-1 text-xs">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} className="font-semibold" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
}

// ─── Chart Card Wrapper ───────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`bg-white rounded-2xl px-6 py-5 shadow-sm border border-slate-100 ${className}`}
    >
      <div className="mb-4">
        <h4
          className="m-0 text-[17px] font-semibold text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h4>
        {subtitle && (
          <p className="mt-0.5 text-xs text-slate-400" style={{ fontFamily: FONT_UI }}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

export default function Summaries() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [scanDetails, setScanDetails] = useState([]);
  const [incidentDetails, setIncidentDetails] = useState([]);
  const [emergencyDetails, setEmergencyDetails] = useState([]);
  const [guardReports, setGuardReports] = useState([]);
  const [routeReports, setRouteReports] = useState([]);

  const [filters, setFilters] = useState({
    date_range: "this_week",
    start_date: "",
    end_date: "",
  });

  const [dashboardData] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/reports/summary?date_range=${filters.date_range}&start_date=${filters.start_date}&end_date=${filters.end_date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");

      setStats(data.stats || {});
      setScanDetails(data.scanDetails || []);
      setIncidentDetails(data.incidentDetails || []);
      setEmergencyDetails(data.emergencyDetails || []);
      setGuardReports(data.guardReports || []);
      setRouteReports(data.routeReports || []);
    } catch (err) {
      console.error("Summary fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchData();
  };

  // ── Derived chart data ──────────────────────────────────────────────────────

  // 1. Guard performance bar chart
  const guardChartData = guardReports.map((r) => ({
    name: r.guard_name || "Unknown",
    Scans: Number(r.total_scans) || 0,
    Checkpoints: Number(r.checkpoints_covered) || 0,
    Routes: Number(r.routes_covered) || 0,
  }));

  // 2. Route coverage bar chart
  const routeChartData = routeReports.map((r) => ({
    name: r.route_name || "Unknown",
    Scans: Number(r.total_scans) || 0,
    Guards: Number(r.guards_assigned) || 0,
    Checkpoints: Number(r.checkpoints_covered) || 0,
  }));

  // 3. Scan activity over time (group by date)
  const scanByDate = scanDetails.reduce((acc, row) => {
    const date = row.scan_date ? String(row.scan_date).slice(0, 10) : "Unknown";
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});
  const scanTimelineData = Object.entries(scanByDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, Scans: count }));

  // 4. Coverage pie: checkpoints covered vs not
  const coveredCheckpoints = Number(stats.covered_checkpoints) || 0;
  const totalCheckpoints = Number(stats.total_checkpoints) || 0;
  const checkpointPieData = [
    { name: "Covered", value: coveredCheckpoints },
    { name: "Remaining", value: Math.max(0, totalCheckpoints - coveredCheckpoints) },
  ];

  // 5. Coverage pie: routes covered vs not
  const coveredRoutes = Number(stats.covered_routes) || 0;
  const totalRoutes = Number(stats.total_routes) || 0;
  const routePieData = [
    { name: "Covered", value: coveredRoutes },
    { name: "Remaining", value: Math.max(0, totalRoutes - coveredRoutes) },
  ];

  // 6. Incident severity breakdown
  const severityCount = incidentDetails.reduce((acc, row) => {
    const sev = (row.severity || "unknown").toLowerCase();
    acc[sev] = (acc[sev] || 0) + 1;
    return acc;
  }, {});
  const incidentSeverityData = Object.entries(severityCount).map(([sev, count]) => ({
    name: sev.charAt(0).toUpperCase() + sev.slice(1),
    value: count,
    fill: INCIDENT_SEVERITY_COLORS[sev] || CHART_COLORS.slate,
  }));

  // 7. Scans per guard (for scan breakdown pie)
  const scansByGuard = scanDetails.reduce((acc, row) => {
    const g = row.guard_name || "Unknown";
    acc[g] = (acc[g] || 0) + 1;
    return acc;
  }, {});
  const scansByGuardData = Object.entries(scansByGuard)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const PIE_MULTI_COLORS = [
    CHART_COLORS.blue,
    CHART_COLORS.green,
    CHART_COLORS.amber,
    CHART_COLORS.purple,
    CHART_COLORS.sky,
    CHART_COLORS.teal,
    CHART_COLORS.red,
    CHART_COLORS.slate,
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading summaries...
        </p>
      </div>
    );
  }

  return (
    <Layout dashboardData={dashboardData} activePage="summaries">
      {/* Page Header */}
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Summaries Module
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          View reports, statistics, and patrol summaries
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
        <h3
          className="m-0 mb-4 text-[18px] font-semibold text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Report Filters
        </h3>
        <form onSubmit={applyFilters} className="flex flex-wrap gap-3 items-end">
          <select
            name="date_range"
            value={filters.date_range}
            onChange={handleChange}
            className="border border-slate-200 px-3 py-3 rounded-xl text-sm text-slate-900 bg-white outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          >
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="custom">Custom</option>
            <option value="all_time">All Time</option>
          </select>

          {filters.date_range === "custom" && (
            <>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleChange}
                className="border border-slate-200 px-3 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                style={{ fontFamily: FONT_UI }}
              />
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleChange}
                className="border border-slate-200 px-3 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                style={{ fontFamily: FONT_UI }}
              />
            </>
          )}

          <button
            type="submit"
            className="px-6 py-3 rounded-xl text-white text-sm font-semibold"
            style={{ background: skin.gradient, fontFamily: FONT_UI }}
          >
            Apply
          </button>
        </form>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
        <h3
          className="m-0 mb-5 text-[18px] font-semibold text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Summary Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Total Guards", value: stats.total_guards, color: "blue" },
            { label: "Active Guards", value: stats.active_guards, color: "green" },
            { label: "Total Routes", value: stats.total_routes, color: "sky" },
            { label: "Covered Routes", value: stats.covered_routes, color: "teal" },
            { label: "Checkpoints", value: stats.total_checkpoints, color: "purple" },
            { label: "Covered CPs", value: stats.covered_checkpoints, color: "green" },
            { label: "Total Scans", value: stats.scanned_qr_codes, color: "blue" },
            { label: "Incidents", value: stats.incidents, color: "amber" },
            { label: "Emergencies", value: stats.emergencies, color: "red" },
            {
              label: "Completion",
              value: `${stats.patrol_completion_rate || 0}%`,
              color: "slate",
            },
          ].map(({ label, value, color }) => (
            <StatCard key={label} label={label} value={value} color={color} />
          ))}
        </div>
      </div>

      {/* ── CHARTS ROW 1: Scan Timeline + Coverage Pies ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
        {/* Scan Activity Over Time */}
        <ChartCard
          title="Scan Activity Over Time"
          subtitle="Daily scan count across the selected period"
          className="xl:col-span-2"
        >
          {scanTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={scanTimelineData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <defs>
                  <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.blue} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={CHART_COLORS.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="Scans"
                  stroke={CHART_COLORS.blue}
                  strokeWidth={2.5}
                  fill="url(#scanGrad)"
                  dot={{ r: 3, fill: CHART_COLORS.blue, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        {/* Coverage Pies */}
        <div className="flex flex-col gap-5">
          <ChartCard title="Checkpoint Coverage" subtitle={`${coveredCheckpoints} / ${totalCheckpoints}`}>
            {totalCheckpoints > 0 ? (
              <ResponsiveContainer width="100%" height={110}>
                <PieChart>
                  <Pie
                    data={checkpointPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={48}
                    paddingAngle={3}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {checkpointPieData.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart small />
            )}
            <DonutLegend items={[
              { label: "Covered", color: PIE_COLORS[0], value: coveredCheckpoints },
              { label: "Remaining", color: PIE_COLORS[1], value: Math.max(0, totalCheckpoints - coveredCheckpoints) },
            ]} />
          </ChartCard>

          <ChartCard title="Route Coverage" subtitle={`${coveredRoutes} / ${totalRoutes}`}>
            {totalRoutes > 0 ? (
              <ResponsiveContainer width="100%" height={110}>
                <PieChart>
                  <Pie
                    data={routePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={48}
                    paddingAngle={3}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {routePieData.map((entry, i) => (
                      <Cell key={i} fill={[CHART_COLORS.green, CHART_COLORS.slate][i]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart small />
            )}
            <DonutLegend items={[
              { label: "Covered", color: CHART_COLORS.green, value: coveredRoutes },
              { label: "Remaining", color: CHART_COLORS.slate, value: Math.max(0, totalRoutes - coveredRoutes) },
            ]} />
          </ChartCard>
        </div>
      </div>

      {/* ── CHARTS ROW 2: Guard Performance + Scans per Guard ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
        <ChartCard
          title="Guard Performance"
          subtitle="Scans, checkpoints & routes covered per guard"
          className="xl:col-span-2"
        >
          {guardChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={guardChartData} margin={{ top: 5, right: 10, left: -20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontFamily: FONT_UI, fontSize: 12, paddingTop: 12 }}
                />
                <Bar dataKey="Scans" fill={CHART_COLORS.blue} radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="Checkpoints" fill={CHART_COLORS.green} radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="Routes" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        {/* Scans by Guard Pie */}
        <ChartCard title="Scans by Guard" subtitle="Share of total scans per guard">
          {scansByGuardData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={scansByGuardData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {scansByGuardData.map((_, i) => (
                      <Cell key={i} fill={PIE_MULTI_COLORS[i % PIE_MULTI_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {scansByGuardData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" style={{ fontFamily: FONT_UI }}>
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: PIE_MULTI_COLORS[i % PIE_MULTI_COLORS.length] }}
                    />
                    <span className="text-slate-600 truncate flex-1">{d.name}</span>
                    <span className="font-semibold text-slate-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </div>

      {/* ── CHARTS ROW 3: Route Analysis + Incident Severity ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">
        <ChartCard
          title="Route-wise Analysis"
          subtitle="Scans, assigned guards & checkpoints per route"
          className="xl:col-span-2"
        >
          {routeChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={routeChartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  width={75}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontFamily: FONT_UI, fontSize: 12, paddingTop: 12 }}
                />
                <Bar dataKey="Scans" fill={CHART_COLORS.sky} radius={[0, 4, 4, 0]} maxBarSize={20} />
                <Bar dataKey="Guards" fill={CHART_COLORS.teal} radius={[0, 4, 4, 0]} maxBarSize={20} />
                <Bar dataKey="Checkpoints" fill={CHART_COLORS.blue} radius={[0, 4, 4, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        {/* Incident Severity */}
        <ChartCard title="Incident Severity" subtitle="Breakdown by severity level">
          {incidentSeverityData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={90}
                  barSize={14}
                  data={incidentSeverityData}
                  startAngle={90}
                  endAngle={-270}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={6}
                    label={false}
                  >
                    {incidentSeverityData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </RadialBar>
                  <Tooltip content={<CustomTooltip />} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-2">
                {incidentSeverityData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2" style={{ fontFamily: FONT_UI }}>
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: d.fill }}
                    />
                    <span className="text-sm text-slate-600 flex-1">{d.name}</span>
                    <span className="text-sm font-semibold text-slate-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </div>

      {/* ── DATA TABLES ── */}
      <TableSection title="Scanning Details">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Guard</Th>
              <Th>Checkpoint</Th>
              <Th>Route</Th>
            </tr>
          </thead>
          <tbody>
            {scanDetails.length ? (
              scanDetails.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <Td>{formatValue(row.scan_date)}</Td>
                  <Td>{formatValue(row.guard_name)}</Td>
                  <Td>{formatValue(row.checkpoint_name)}</Td>
                  <Td>{formatValue(row.route_name)}</Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={4} />
            )}
          </tbody>
        </table>
      </TableSection>

      <TableSection title="Guard Wise Patrol Report">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Guard</Th>
              <Th>Total Scans</Th>
              <Th>Checkpoints Covered</Th>
              <Th>Routes Covered</Th>
            </tr>
          </thead>
          <tbody>
            {guardReports.length ? (
              guardReports.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <Td>{formatValue(row.guard_name)}</Td>
                  <Td>{formatValue(row.total_scans)}</Td>
                  <Td>{formatValue(row.checkpoints_covered)}</Td>
                  <Td>{formatValue(row.routes_covered)}</Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={4} />
            )}
          </tbody>
        </table>
      </TableSection>

      <TableSection title="Route Wise Patrol Report">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Route</Th>
              <Th>Total Scans</Th>
              <Th>Guards Assigned</Th>
              <Th>Checkpoints Covered</Th>
            </tr>
          </thead>
          <tbody>
            {routeReports.length ? (
              routeReports.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <Td>{formatValue(row.route_name)}</Td>
                  <Td>{formatValue(row.total_scans)}</Td>
                  <Td>{formatValue(row.guards_assigned)}</Td>
                  <Td>{formatValue(row.checkpoints_covered)}</Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={4} />
            )}
          </tbody>
        </table>
      </TableSection>

      <TableSection title="Incident Reports">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Guard</Th>
              <Th>Type</Th>
              <Th>Description</Th>
              <Th>Severity</Th>
            </tr>
          </thead>
          <tbody>
            {incidentDetails.length ? (
              incidentDetails.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <Td>{formatValue(row.incident_date)}</Td>
                  <Td>{formatValue(row.guard_name)}</Td>
                  <Td>{formatValue(row.incident_type)}</Td>
                  <Td>{formatValue(row.description)}</Td>
                  <Td>
                    <SeverityBadge value={row.severity} />
                  </Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={5} />
            )}
          </tbody>
        </table>
      </TableSection>

      <TableSection title="Emergency Alerts">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Guard</Th>
              <Th>Type</Th>
              <Th>Description</Th>
              <Th>Priority</Th>
            </tr>
          </thead>
          <tbody>
            {emergencyDetails.length ? (
              emergencyDetails.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <Td>{formatValue(row.emergency_date)}</Td>
                  <Td>{formatValue(row.guard_name)}</Td>
                  <Td>{formatValue(row.emergency_type)}</Td>
                  <Td>{formatValue(row.description)}</Td>
                  <Td>
                    <SeverityBadge value={row.priority} />
                  </Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={5} />
            )}
          </tbody>
        </table>
      </TableSection>
    </Layout>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    teal: "bg-teal-50 text-teal-700",
    purple: "bg-purple-50 text-purple-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-xl px-4 py-4 ${colorMap[color] || colorMap.slate}`}>
      <p className="text-xs font-medium mb-1 opacity-70" style={{ fontFamily: FONT_UI }}>
        {label}
      </p>
      <p className="text-2xl font-bold m-0" style={{ fontFamily: FONT_HEADING }}>
        {formatValue(value)}
      </p>
    </div>
  );
}

function DonutLegend({ items }) {
  return (
    <div className="flex gap-4 mt-2 justify-center">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5 text-xs" style={{ fontFamily: FONT_UI }}>
          <span className="w-2 h-2 rounded-full" style={{ background: item.color }} />
          <span className="text-slate-500">{item.label}</span>
          <span className="font-semibold text-slate-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function SeverityBadge({ value }) {
  if (!value) return <span className="text-slate-400 text-sm">-</span>;
  const v = String(value).toLowerCase();
  const styles = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-green-100 text-green-700",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[v] || "bg-slate-100 text-slate-600"}`}
      style={{ fontFamily: FONT_UI }}
    >
      {value}
    </span>
  );
}

function EmptyChart({ small = false }) {
  return (
    <div
      className={`flex items-center justify-center text-slate-400 text-sm rounded-xl bg-slate-50 ${
        small ? "h-[110px]" : "h-[200px]"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      No data available
    </div>
  );
}

function TableSection({ title, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 overflow-x-auto">
      <h3
        className="m-0 mb-4 text-[18px] font-semibold text-slate-900"
        style={{ fontFamily: FONT_HEADING }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 bg-slate-50 first:rounded-tl-lg last:rounded-tr-lg"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-4 py-3 text-sm text-slate-700 border-b border-slate-100"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function EmptyRow({ col }) {
  return (
    <tr>
      <td
        colSpan={col}
        className="text-center py-8 text-slate-400 text-sm"
        style={{ fontFamily: FONT_UI }}
      >
        No data available
      </td>
    </tr>
  );
}